/* eslint-disable max-lines-per-function */
import { useEffect, useRef, useState } from 'react';

import type { AiModelsCatalogModel } from '@/domain/models/ai-model.js';
import { DEFAULT_THEME_COLORS, type ThemeColors } from '@/domain/models/theme.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';
import { FALLBACK_DEFAULT_AI_MODEL } from '@/shared/constants/ai-models.js';
import { fileToBase64 } from '@/shared/utils/file-to-base64.js';

export type ClientFormState = {
    name: string;
    abbreviation: string;
    aiModel: string;
    aiStrictnessLevel: string;
    aiExtraSystemPrompt: string;
    slaHoursEstimateEf: string;
    slaHoursStartDev: string;
    slaHoursAnalyzeBug: string;
    slaHoursAnalyzeMinor: string;
    slaDaysValidateDoc: string;
    slaMinScoreAi: string;
    emailDomains: string[];
    themeEnabled: boolean;
    themeColors: ThemeColors;
};

const emptyForm = (defaultAiModel: string): ClientFormState => ({
    name: '',
    abbreviation: '',
    aiModel: defaultAiModel,
    aiStrictnessLevel: '5',
    aiExtraSystemPrompt: '',
    slaHoursEstimateEf: '5',
    slaHoursStartDev: '5',
    slaHoursAnalyzeBug: '8',
    slaHoursAnalyzeMinor: '16',
    slaDaysValidateDoc: '5',
    slaMinScoreAi: '7',
    emailDomains: [''],
    themeEnabled: false,
    themeColors: { ...DEFAULT_THEME_COLORS }
});

export function useClientForm(controller: ClientController, aiModelsCatalog: AiModelsCatalogModel | null, open: boolean, isEdit: boolean, clientId: string | undefined) {
    const [form, setForm] = useState<ClientFormState>(() => emptyForm(aiModelsCatalog?.defaultModelId ?? FALLBACK_DEFAULT_AI_MODEL));
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [createdAt, setCreatedAt] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [logoError, setLogoError] = useState<string | null>(null);

    const aiModelsCatalogRef = useRef(aiModelsCatalog);
    useEffect(() => {
        aiModelsCatalogRef.current = aiModelsCatalog;
    }, [aiModelsCatalog]);
    const resolveDefaultAiModel = () => aiModelsCatalogRef.current?.defaultModelId ?? FALLBACK_DEFAULT_AI_MODEL;

    const setField = <K extends keyof ClientFormState>(key: K, value: ClientFormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        if (!open) {
            return;
        }
        setForm(emptyForm(resolveDefaultAiModel()));
        setLogoUrl(null);
        setLogoFile(null);
        setCreatedAt('');
        setLoadError(null);
        setLogoError(null);
        if (isEdit && clientId) {
            setLoading(true);
            controller
                .loadClientById(clientId)
                .then((result) => {
                    result.fold(
                        (error) => {
                            setLoadError(error.message || '');
                        },
                        (client) => {
                            setForm({
                                name: client.name,
                                abbreviation: client.abbreviation,
                                aiModel: client.aiModel || resolveDefaultAiModel(),
                                aiStrictnessLevel: String(client.aiStrictnessLevel ?? 5),
                                aiExtraSystemPrompt: client.aiExtraSystemPrompt ?? '',
                                slaHoursEstimateEf: String(client.slaHoursEstimateEf ?? 5),
                                slaHoursStartDev: String(client.slaHoursStartDev ?? 5),
                                slaHoursAnalyzeBug: String(client.slaHoursAnalyzeBug ?? 8),
                                slaHoursAnalyzeMinor: String(client.slaHoursAnalyzeMinor ?? 16),
                                slaDaysValidateDoc: String(client.slaDaysValidateDoc ?? 5),
                                slaMinScoreAi: String(client.slaMinScoreAi ?? 7),
                                emailDomains: client.emailDomains.length > 0 ? [...client.emailDomains] : [''],
                                themeEnabled: Boolean(client.themeColors),
                                themeColors: client.themeColors ? { ...client.themeColors } : { ...DEFAULT_THEME_COLORS }
                            });
                            setLogoUrl(client.logoUrl);
                            setCreatedAt(client.createdAt);
                        }
                    );
                })
                .catch(() => {
                    setLoadError('');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open, isEdit, clientId, controller]);

    const uploadLogo = async () => {
        if (!logoFile || !clientId) {
            return;
        }
        setLogoError(null);
        try {
            const fileData = await fileToBase64(logoFile);
            const result = await controller.uploadLogo(clientId, { fileData, fileName: logoFile.name, mimeType: logoFile.type });
            if (result.isRight()) {
                setLogoUrl(result.value.logoUrl);
                setLogoFile(null);
            } else {
                setLogoError(result.value.message || '');
            }
        } catch {
            setLogoError('');
        }
    };

    const removeLogo = async () => {
        if (!clientId) {
            return;
        }
        setLogoError(null);
        try {
            const result = await controller.removeLogo(clientId);
            if (result.isRight()) {
                setLogoUrl(null);
                setLogoFile(null);
            } else {
                setLogoError(result.value.message || '');
            }
        } catch {
            setLogoError('');
        }
    };

    return { form, setField, logoUrl, logoFile, setLogoFile, createdAt, loading, loadError, logoError, uploadLogo, removeLogo };
}
